/* Arteriel AI storefront JS — App Proxy-backed search.
 *
 * Security invariant (see themes/arteriel-theme/README.md): all rendering of
 * untrusted data uses document.createElement + textContent + setAttribute.
 * Do NOT introduce innerHTML anywhere in this file — a repo hook rejects it.
 *
 * The pure helpers (buildCard / render / clear) also live in
 * assets/arteriel-ai-helpers.mjs for unit testing. Keep the two copies in sync.
 */
(function () {
  "use strict";
  // Market subfolder prefix (assets/locale-links.js); falls back to the bare path.
  var localePath = function (p) { return window.arterielLocalePath ? window.arterielLocalePath(p) : p; };

  function $id(id) {
    return document.getElementById(id);
  }

  function clear(node) {
    while (node.firstChild) node.removeChild(node.firstChild);
  }

  function buildCard(product) {
    var a = document.createElement("a");
    a.className = "arteriel-search__card";
    a.setAttribute("href", "/products/" + product.handle);
    a.setAttribute("aria-label", product.title);

    if (product.image && product.image.url) {
      var img = document.createElement("img");
      img.setAttribute("src", product.image.url);
      img.setAttribute("alt", product.image.alt || product.title);
      img.setAttribute("loading", "lazy");
      img.setAttribute("width", "400");
      a.appendChild(img);
    }

    var span = document.createElement("span");
    span.className = "arteriel-search__title";
    span.textContent = product.title;
    a.appendChild(span);
    return a;
  }

  function render(resultsEl, products) {
    clear(resultsEl);
    if (!products || !products.length) {
      var empty = document.createElement("p");
      empty.className = "arteriel-search__empty";
      empty.textContent = "No results";
      resultsEl.appendChild(empty);
      return;
    }
    var grid = document.createElement("div");
    grid.className = "arteriel-search__grid";
    products.forEach(function (p) {
      grid.appendChild(buildCard(p));
    });
    resultsEl.appendChild(grid);
  }

  async function fetchResults(proxy, q) {
    var url = proxy + "/search?q=" + encodeURIComponent(q) + "&limit=24";
    var res = await fetch(url, { credentials: "same-origin" });
    if (!res.ok) throw new Error("search failed: " + res.status);
    var data = await res.json();
    return data.results || [];
  }

  // Each API result is { product_id, handle, score }. Title/image come from
  // Shopify's storefront endpoint /products/<handle>.js — it serves by HANDLE
  // only (a numeric id always 404s), so a result without a handle is skipped.
  async function hydrateProducts(results) {
    var items = await Promise.all(
      results.map(function (r) {
        if (!r || !r.handle) return null;
        return fetch("/products/" + encodeURIComponent(r.handle) + ".js")
          .then(function (res) { return res.ok ? res.json() : null; })
          .then(function (p) {
            if (!p) return null;
            return {
              title: p.title,
              handle: p.handle,
              image: p.featured_image
                ? { url: p.featured_image, alt: p.title }
                : null,
            };
          })
          .catch(function () { return null; });
      }),
    );
    return items.filter(Boolean);
  }

  function fallbackToNative(q) {
    window.location.href = localePath("/search?q=" + encodeURIComponent(q));
  }

  function init() {
    var container = document.querySelector(".arteriel-search");
    if (!container) return;
    var proxy = container.getAttribute("data-proxy") || "/apps/arteriel-ai";
    var input = $id("arteriel-search-input");
    var button = container.querySelector(".arteriel-search__submit");
    var results = $id("arteriel-search-results");
    if (!input || !button || !results) return;

    async function onSubmit() {
      var q = (input.value || "").trim();
      if (!q) return;
      try {
        var raw = await fetchResults(proxy, q);
        var products = await hydrateProducts(raw);
        render(results, products);
      } catch (err) {
        // Network/5xx/JSON parse — fall back to Shopify's native search so
        // the storefront remains usable when the AI path is down.
        // eslint-disable-next-line no-console
        console.warn("[arteriel-ai] search fell back to native:", err);
        fallbackToNative(q);
      }
    }

    button.addEventListener("click", onSubmit);
    input.addEventListener("keydown", function (e) {
      if (e.key === "Enter") {
        e.preventDefault();
        onSubmit();
      }
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();

// --- Image-search flow (Plan 7 Task 4) ---
// Mirrors the pure helpers from assets/arteriel-ai-helpers.mjs
// (validateImageFile / buildPresignedUploadURL / buildVisualSearchURL /
//  buildImageResultCard). Keep in sync.
(function () {
  "use strict";
  var MAX_IMAGE_BYTES = 10 * 1024 * 1024;

  function buildCard(product) {
    var a = document.createElement("a");
    a.className = "arteriel-image-card";
    a.setAttribute("href", product.url || ("/products/" + (product.handle || "")));
    if (typeof product.score === "number") {
      a.dataset.score = product.score.toFixed(2);
    }
    if (product.image && product.image.url) {
      var img = document.createElement("img");
      img.setAttribute("src", product.image.url);
      img.setAttribute("alt", product.image.alt || product.title || "");
      img.setAttribute("loading", "lazy");
      a.appendChild(img);
    }
    var title = document.createElement("span");
    title.className = "arteriel-image-card__title";
    title.textContent = product.title || "";
    a.appendChild(title);
    return a;
  }

  function clear(node) {
    while (node.firstChild) node.removeChild(node.firstChild);
  }

  function getOrCreateSession() {
    try {
      var s = sessionStorage.getItem("arteriel-sess");
      if (s) return s;
    } catch (e) { /* sessionStorage unavailable — fall through */ }
    var fresh = (typeof crypto !== "undefined" && crypto.randomUUID)
      ? crypto.randomUUID()
      : String(Date.now()) + "-" + Math.random().toString(36).slice(2);
    try { sessionStorage.setItem("arteriel-sess", fresh); } catch (e) { /* ignore */ }
    return fresh;
  }

  // /products/<x>.js serves by HANDLE only — a numeric id always 404s.
  async function hydrate(r) {
    if (!r || !r.handle) return null;
    var res = await fetch("/products/" + encodeURIComponent(r.handle) + ".js");
    if (!res.ok) return null;
    var p = await res.json();
    return {
      title: p.title,
      handle: p.handle,
      url: p.url,
      image: p.featured_image ? { url: p.featured_image, alt: p.title } : null,
    };
  }

  async function handleFile(f, proxy, statusEl, resultsEl) {
    if (!f || !f.type || !f.type.startsWith("image/")) {
      statusEl.textContent = "Please upload an image (JPEG or PNG).";
      return;
    }
    if (f.size > MAX_IMAGE_BYTES) {
      statusEl.textContent = "Image is too large. Please upload under 10 MB.";
      return;
    }

    statusEl.textContent = "Uploading\u2026";
    clear(resultsEl);

    try {
      var session = getOrCreateSession();

      var urlResp = await fetch(
        proxy + "/upload-url?session_id=" + encodeURIComponent(session),
        { credentials: "same-origin" },
      );
      if (!urlResp.ok) throw new Error("upload-url HTTP " + urlResp.status);
      var urlJson = await urlResp.json();
      var presignedUrl = urlJson.url;
      var s3Key = urlJson.key;

      var putResp = await fetch(presignedUrl, {
        method: "PUT",
        headers: { "Content-Type": "image/jpeg" },
        body: f,
      });
      if (!putResp.ok) throw new Error("S3 PUT HTTP " + putResp.status);

      statusEl.textContent = "Finding similar art\u2026";
      var vsResp = await fetch(
        proxy + "/visual-search?s3_key=" + encodeURIComponent(s3Key),
        { credentials: "same-origin" },
      );
      if (vsResp.status === 422) {
        statusEl.textContent =
          "This image can't be used for search. Please try a different one.";
        return;
      }
      if (!vsResp.ok) throw new Error("visual-search HTTP " + vsResp.status);
      var vsJson = await vsResp.json();
      var hits = vsJson.results || [];

      if (!hits.length) {
        statusEl.textContent = "No similar artworks found. Try a different image.";
        return;
      }

      statusEl.textContent = "Found " + hits.length + " similar artworks.";

      var cards = await Promise.all(hits.map(async function (r) {
        var prod = await hydrate(r);
        if (!prod) return null;
        prod.score = r.score;
        return buildCard(prod);
      }));
      cards = cards.filter(Boolean);
      clear(resultsEl);
      cards.forEach(function (c) { resultsEl.appendChild(c); });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("[arteriel-ai] visual search failed:", err);
      statusEl.textContent = "Something went wrong. Please try again.";
    }
  }

  function init() {
    var root = document.getElementById("arteriel-image-search");
    if (!root) return;
    var proxy = root.dataset.proxy || "/apps/arteriel-ai";
    var drop = root.querySelector(".arteriel-dropzone");
    var file = document.getElementById("arteriel-file");
    var status = document.getElementById("arteriel-image-status");
    var results = document.getElementById("arteriel-image-results");
    if (!drop || !file || !status || !results) return;

    ["dragenter", "dragover"].forEach(function (evt) {
      drop.addEventListener(evt, function (e) {
        e.preventDefault();
        drop.classList.add("dragover");
      });
    });
    ["dragleave", "drop"].forEach(function (evt) {
      drop.addEventListener(evt, function (e) {
        e.preventDefault();
        drop.classList.remove("dragover");
      });
    });
    drop.addEventListener("drop", function (e) {
      var f = e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files[0];
      if (f) handleFile(f, proxy, status, results);
    });
    file.addEventListener("change", function (e) {
      var f = e.target && e.target.files && e.target.files[0];
      if (f) handleFile(f, proxy, status, results);
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();

// --- Personalization: event tracking + recommendations (Plan 8 Task 6) ---
// Mirrors consentValue / buildRecommendationsURL / buildEventPayload from
// assets/arteriel-ai-helpers.mjs. Keep in sync. Security invariant: no
// innerHTML on remote data — all product fields are rendered via
// createElement + textContent + setAttribute.
(function () {
  "use strict";
  var CONSENT_KEY = "arteriel-consent";
  var REC_LIMIT = 12;

  function consentValue() {
    try {
      var v = localStorage.getItem(CONSENT_KEY);
      return v === "granted" || v === "denied" ? v : null;
    } catch (e) {
      return null;
    }
  }

  function getOrCreateSession() {
    try {
      var s = sessionStorage.getItem("arteriel-sess");
      if (s) return s;
    } catch (e) { /* ignore */ }
    var fresh = (typeof crypto !== "undefined" && crypto.randomUUID)
      ? crypto.randomUUID()
      : String(Date.now()) + "-" + Math.random().toString(36).slice(2);
    try { sessionStorage.setItem("arteriel-sess", fresh); } catch (e) { /* ignore */ }
    return fresh;
  }

  function customerIdFromDOM() {
    var el = document.getElementById("arteriel-recommendations");
    if (!el) return null;
    var raw = el.getAttribute("data-customer-id");
    if (!raw) return null;
    var trimmed = String(raw).trim();
    return trimmed && trimmed !== "0" ? trimmed : null;
  }

  function buildEventPayload(sessionId, customerId, eventType, productId) {
    var out = {
      session_id: sessionId,
      event_type: eventType,
      product_id: productId,
    };
    if (customerId) out.customer_id = customerId;
    return out;
  }

  function buildRecommendationsURL(proxy, opts) {
    var qs = new URLSearchParams();
    if (opts.customer_id) {
      qs.set("customer_id", String(opts.customer_id));
    } else if (opts.session_id) {
      qs.set("session_id", String(opts.session_id));
    }
    if (opts.limit) qs.set("limit", String(opts.limit));
    // The shopper's Shopify market id: the API recommends only works it sells.
    if (opts.market) qs.set("market", String(opts.market));
    return proxy + "/recommendations?" + qs.toString();
  }

  function trackEvent(proxy, eventType, productId) {
    if (consentValue() !== "granted") return;
    var session = getOrCreateSession();
    var customer = customerIdFromDOM();
    var body = buildEventPayload(session, customer, eventType, productId);
    // App Proxy signs the URL automatically when we use same-origin creds.
    fetch(proxy + "/event", {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    }).catch(function () { /* swallow — telemetry is best-effort */ });
  }

  function buildRecCard(product) {
    var a = document.createElement("a");
    a.className = "arteriel-rec-card";
    a.setAttribute("href", "/products/" + (product.handle || ""));
    a.setAttribute("aria-label", product.title || "");
    if (product.image && product.image.url) {
      var img = document.createElement("img");
      img.setAttribute("src", product.image.url);
      img.setAttribute("alt", product.image.alt || product.title || "");
      img.setAttribute("loading", "lazy");
      img.setAttribute("width", "400");
      a.appendChild(img);
    }
    var span = document.createElement("span");
    span.className = "arteriel-rec-card__title";
    span.textContent = product.title || "";
    a.appendChild(span);
    return a;
  }

  function clearNode(node) {
    while (node.firstChild) node.removeChild(node.firstChild);
  }

  // /products/<x>.js serves by HANDLE only — a numeric id always 404s, so a
  // result without a handle is skipped rather than requested.
  async function hydrate(r) {
    if (!r || !r.handle) return null;
    try {
      var res = await fetch("/products/" + encodeURIComponent(r.handle) + ".js");
      if (!res.ok) return null;
      var p = await res.json();
      return {
        title: p.title,
        handle: p.handle,
        image: p.featured_image ? { url: p.featured_image, alt: p.title } : null,
      };
    } catch (e) {
      return null;
    }
  }

  async function loadRecs(proxy) {
    if (consentValue() !== "granted") return;
    var grid = document.getElementById("arteriel-recs");
    if (!grid) return;
    var session = getOrCreateSession();
    var customer = customerIdFromDOM();
    var recRoot = document.getElementById("arteriel-recommendations");
    var url = buildRecommendationsURL(proxy, {
      customer_id: customer,
      session_id: customer ? null : session,
      limit: REC_LIMIT,
      market: recRoot ? recRoot.getAttribute("data-market") : null,
    });
    try {
      var res = await fetch(url, { credentials: "same-origin" });
      if (!res.ok) return;
      var data = await res.json();
      var hits = (data.results || data.recommendations || []).slice(0, REC_LIMIT);
      var cards = await Promise.all(hits.map(async function (r) {
        var prod = await hydrate(r);
        return prod ? buildRecCard(prod) : null;
      }));
      clearNode(grid);
      cards.filter(Boolean).forEach(function (c) { grid.appendChild(c); });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn("[arteriel-ai] recs failed:", err);
    }
  }

  function initPersonalization() {
    var proxy = "/apps/arteriel-ai";
    var recRoot = document.getElementById("arteriel-recommendations");
    if (recRoot && recRoot.dataset.proxy) proxy = recRoot.dataset.proxy;

    // 1. Product-page view events (gated on consent inside trackEvent).
    var ogType = document.querySelector('meta[property="og:type"][content="product"]');
    var productEl = document.querySelector("[data-product-id]");
    if (ogType && productEl) {
      var pid = productEl.getAttribute("data-product-id");
      if (pid) trackEvent(proxy, "view", pid);
    }

    // 2. Add-to-cart submits.
    var cartForms = document.querySelectorAll('form[action*="/cart/add"]');
    cartForms.forEach(function (f) {
      f.addEventListener("submit", function () {
        var input = f.querySelector('input[name="id"]');
        var variantId = input ? input.value : null;
        var anchor = productEl ? productEl.getAttribute("data-product-id") : null;
        // PRODUCT id first: trending_score ranks product ids, and a variant id is
        // one no product has (2026-09-06). The variant is a last resort only.
        trackEvent(proxy, "add_to_cart", anchor || variantId);
      });
    });

    // 3. Recs on initial paint.
    if (recRoot) loadRecs(proxy);

    // 4. React to consent changes mid-session.
    document.addEventListener("arteriel-consent", function (ev) {
      if (ev && ev.detail === "granted" && recRoot) loadRecs(proxy);
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initPersonalization);
  } else {
    initPersonalization();
  }
})();
